import BusinessIcon from "@mui/icons-material/Business";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

import { toJalaliFullDate } from "../../../../../middleware/middleware";
import { STEP_STATUS, STEP_STATUS_CHIP_COLOR } from "./loanMehrsanStageConstants";
import { getNextActionableStep } from "./loanMehrsanStageAdapter";

const sectionThemes = {
  mehrsan: {
    icon: BusinessIcon,
    color: "info.main",
    progressColor: "info",
  },
  loan: {
    icon: CreditCardIcon,
    color: "secondary.main",
    progressColor: "secondary",
  },
};

const LoanMehrsanProcessStepsTable = ({
  domain = "mehrsan",
  title,
  steps = [],
  loading = false,
  footerNote = "",
  headerExtra = null,
  onEditStep,
  onAdvanceStep,
  canEditSteps = true,
  readOnly = false,
}) => {
  const theme = sectionThemes[domain] || sectionThemes.mehrsan;
  const Icon = theme.icon;
  const completed = steps.filter((s) => s.status === "completed").length;
  const total = steps.length;
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
  const actorHeader = domain === "loan" ? "مسئول اقدام" : "مسئول";
  const codeHeader = domain === "loan" ? "کد رهگیری" : "کد رهگیری / مرجع";
  const currentStep = getNextActionableStep(steps);
  const currentStepId = currentStep?.id ?? null;
  const showAdvance = Boolean(onAdvanceStep) && domain === "mehrsan";

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Icon sx={{ color: theme.color }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              وضعیت کلی: {completed} از {total} تکمیل شده
            </Typography>
            {showAdvance && canEditSteps && !readOnly && currentStep ? (
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<SkipNextIcon />}
                onClick={() => onAdvanceStep?.(currentStep)}
              >
                مرحله بعد
              </Button>
            ) : null}
          </Stack>
        </Stack>
        {headerExtra ? <Box sx={{ mt: 1.25 }}>{headerExtra}</Box> : null}
        {currentStep && domain === "mehrsan" ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            مرحله جاری قابل اقدام: {currentStep.title}
          </Typography>
        ) : null}
        <LinearProgress
          variant="determinate"
          value={progress}
          color={theme.progressColor}
          sx={{ mt: 1.5, height: 8, borderRadius: 1 }}
        />
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell width={48}>ردیف</TableCell>
              <TableCell>مرحله</TableCell>
              <TableCell>{actorHeader}</TableCell>
              <TableCell width={130}>وضعیت</TableCell>
              <TableCell width={110}>تاریخ</TableCell>
              <TableCell width={140}>{codeHeader}</TableCell>
              <TableCell>توضیحات</TableCell>
              {!readOnly ? (
                <TableCell width={showAdvance ? 200 : 120} align="center">
                  عملیات
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={readOnly ? 7 : 8} align="center">
                  در حال بارگذاری...
                </TableCell>
              </TableRow>
            ) : steps.length === 0 ? (
              <TableRow>
                <TableCell colSpan={readOnly ? 7 : 8} align="center">
                  مرحله‌ای تعریف نشده است.
                </TableCell>
              </TableRow>
            ) : (
              steps.map((step, index) => {
                const isCurrent = step.id === currentStepId;
                const isDone = step.status === STEP_STATUS.COMPLETED;
                return (
                  <TableRow
                    key={step.id}
                    hover
                    sx={
                      isCurrent
                        ? { bgcolor: "warning.50", "& td": { borderColor: "warning.light" } }
                        : undefined
                    }
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={isCurrent ? 800 : 600}>
                        {step.title}
                        {isCurrent ? (
                          <Chip
                            size="small"
                            label="جاری"
                            color="warning"
                            sx={{ ml: 1, height: 20 }}
                          />
                        ) : null}
                      </Typography>
                    </TableCell>
                    <TableCell>{step.responsibleLabel || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={step.statusLabel}
                        color={STEP_STATUS_CHIP_COLOR[step.status] || "default"}
                        variant={isDone ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      {step.date ? toJalaliFullDate(step.date) : "—"}
                    </TableCell>
                    <TableCell>{step.referenceCode || "—"}</TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        {step.description ? (
                          <Typography variant="caption" color="text.secondary">
                            {step.description}
                          </Typography>
                        ) : null}
                        {step.attachmentName ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <DescriptionOutlinedIcon
                              sx={{ fontSize: 16, color: "text.secondary" }}
                            />
                            <Typography variant="caption">
                              {step.attachmentName}
                            </Typography>
                          </Stack>
                        ) : null}
                        {!step.description && !step.attachmentName ? "—" : null}
                      </Stack>
                    </TableCell>
                    {!readOnly ? (
                      <TableCell align="center">
                        {canEditSteps ? (
                          <Stack
                            direction="row"
                            spacing={0.75}
                            justifyContent="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {showAdvance && isCurrent ? (
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                startIcon={<SkipNextIcon />}
                                onClick={() => onAdvanceStep?.(step)}
                              >
                                مرحله بعد
                              </Button>
                            ) : null}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditOutlinedIcon />}
                              onClick={() => onEditStep?.(step)}
                            >
                              {isDone ? "ویرایش" : "ثبت"}
                            </Button>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            غیرفعال
                          </Typography>
                        )}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {footerNote ? (
        <Box sx={{ px: 2, py: 1.25, bgcolor: "grey.50" }}>
          <Typography variant="caption" color="text.secondary">
            {footerNote}
          </Typography>
        </Box>
      ) : null}
    </Paper>
  );
};

LoanMehrsanProcessStepsTable.propTypes = {
  domain: PropTypes.oneOf(["mehrsan", "loan"]),
  title: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  footerNote: PropTypes.string,
  headerExtra: PropTypes.node,
  onEditStep: PropTypes.func,
  onAdvanceStep: PropTypes.func,
  canEditSteps: PropTypes.bool,
  readOnly: PropTypes.bool,
};

export default LoanMehrsanProcessStepsTable;
